import React from "react";
import {
  BellRing,
  Calculator,
  ClipboardList,
  Layers,
  LineChart,
  MessagesSquare,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import Section from "../components/Section";
import SectionHeading from "../components/SectionHeading";
import { SECTION_IDS } from "../landingConfig";

/**
 * The six-step pipeline: horizontal on desktop, a vertical timeline on mobile.
 *
 * The connecting line is a thin static border, not an animated arrow.
 */
const STEPS = [
  { key: "s1", Icon: ClipboardList },
  { key: "s2", Icon: Calculator },
  { key: "s3", Icon: Layers },
  { key: "s4", Icon: BellRing },
  { key: "s5", Icon: MessagesSquare },
  { key: "s6", Icon: LineChart },
];

export default function HowItWorks() {
  const { t } = useTranslation();

  return (
    <Section id={SECTION_IDS.how}>
      <SectionHeading
        eyebrow={t("landing.how.eyebrow")}
        title={t("landing.how.h2")}
      />

      <ol className="mt-12 grid gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-6">
        {STEPS.map(({ key, Icon }, i) => (
          <li
            key={key}
            className="landing-reveal relative flex gap-4 lg:flex-col lg:gap-0"
            data-reveal-index={i}
          >
            {/*
              Connector to the *next* step: a vertical rail on mobile, a
              horizontal rule on desktop. Omitted on the last step — there is
              nothing after it to connect to, and on desktop a trailing rule
              would run past the container edge.
            */}
            {i < STEPS.length - 1 && (
              <span
                aria-hidden="true"
                className="absolute left-5 top-11 h-[calc(100%-1rem)] w-px bg-line lg:left-11 lg:top-5 lg:h-px lg:w-full"
              />
            )}

            <span className="relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line bg-white">
              <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
            </span>

            <div className="lg:mt-5 lg:pr-6">
              <p className="text-sm font-medium tabular-nums text-primary">
                {String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="mt-1 text-base font-semibold leading-snug text-black-landing">
                {t(`landing.how.${key}_title`)}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted">
                {t(`landing.how.${key}_body`)}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </Section>
  );
}
